import { useState } from 'react';
import { clsx } from 'clsx';

import styles from './RadioGroup.module.css';

import { RadioButton } from 'components/RadioButton/RadioButton';

type Option = {
  id: string;
  titleLabel: string;
  descriptionLabel?: string;
  value: string;
};

export interface RadioGroupProps {
  /** The label of the radio group */
  label: string;
  /** Radio options. */
  options: Option[];
  /** Returns selected values as string */
  onChange: (value: string) => void;
  /** If true, the whole group will be disabled. */
  disabled?: boolean;
}

/** Represents a component for dispalying grouped styled radio buttons. */
export const RadioGroup = ({ label, options, onChange, disabled }: RadioGroupProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const handleSelection = (value: string) => {
    if (!disabled) {
      setSelectedValue(value);
      onChange(value);
    }
  };

  return (
    <div className={styles['radio-group__wrapper']}>
      <h2 className={styles['radio-group__header']}>{label}</h2>
      <div className={styles['radio-group']}>
        {options.map(({ id, descriptionLabel, titleLabel, value }) => (
          <div
            className={clsx(styles['radio-group__option'], {
              [styles['radio-group__option--disabled']]: disabled,
            })}
            key={id}
            onClick={() => handleSelection(value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                handleSelection(value);
              }
            }}
            role="presentation"
          >
            <RadioButton
              id={id}
              label={titleLabel}
              description={descriptionLabel}
              name={label}
              onChange={(event) => {
                handleSelection(event.target.value);
              }}
              value={value}
              checked={selectedValue === value}
              disabled={disabled}
              gap="large"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
